import { useState, useEffect } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { Save, CheckCircle2, Lock } from 'lucide-react';

export function CompanySettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [taxRegime, setTaxRegime] = useState('GST_INDIA');
  const [taxRegistrationNumber, setTaxRegistrationNumber] = useState('');
  const [costingPolicy, setCostingPolicy] = useState('FIFO');
  const [isCostPolicyLocked, setIsCostPolicyLocked] = useState(false);

  useEffect(() => {
    fetch('/api/accounting/org/company')
      .then((res) => res.json())
      .then((data) => {
        if (data.ok || data.success) {
          const comp = data.data?.company;
          if (comp) {
            setLegalName(comp.legalName || '');
            setTradeName(comp.tradeName || '');
            setTaxRegime(comp.taxRegime || 'GST_INDIA');
            setTaxRegistrationNumber(comp.taxRegistrationNumber || '');
            setCostingPolicy(comp.costingPolicy || 'FIFO');
            setIsCostPolicyLocked(!!comp.isCostPolicyLocked);
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch('/api/accounting/org/company', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          legalName,
          tradeName,
          taxRegime,
          taxRegistrationNumber,
          costingPolicy,
        }),
      });
      const data = await res.json();
      if (res.ok && (data.ok || data.success)) {
        setToast('Company configuration saved successfully!');
        setTimeout(() => setToast(null), 3000);
      } else {
        alert(data.error?.message || 'Failed to save company settings.');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {toast && (
          <div className="p-4 bg-success/10 dark:bg-success border border-success/25 dark:border-success/40 rounded-xl text-success-text dark:text-success-text text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-text" />
            {toast}
          </div>
        )}

        <form onSubmit={handleSave} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
          <div className="border-b border-slate-200 dark:border-slate-800 pb-4">
            <h2 className="text-lg font-bold">Company Profile & Legal Entity</h2>
            <p className="text-xs text-slate-500">Legal entity name, tax registration identifiers, and stock costing policy.</p>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading company profile...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Legal Company Name *</label>
                  <input
                    type="text"
                    required
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Trade / Doing Business As (DBA) Name</label>
                  <input
                    type="text"
                    value={tradeName}
                    onChange={(e) => setTradeName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Default Tax Regime</label>
                  <select
                    value={taxRegime}
                    onChange={(e) => setTaxRegime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm"
                  >
                    <option value="GST_INDIA">India GST (CGST + SGST / IGST)</option>
                    <option value="VAT_GCC">GCC VAT (5% / 15%)</option>
                    <option value="US_SALES_TAX">US State Sales Tax</option>
                    <option value="EXEMPT">Tax Exempt / Zero Rate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Tax Registration Identifier (GSTIN / VAT ID)</label>
                  <input
                    type="text"
                    placeholder="e.g. 27AAAAA0000A1Z5"
                    value={taxRegistrationNumber}
                    onChange={(e) => setTaxRegistrationNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Financial Year</label>
                  <select defaultValue="2026-27" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <option value="2026-27">FY 2026-27 (Apr 2026 - Mar 2027)</option>
                    <option value="2025-26">FY 2025-26 (Apr 2025 - Mar 2026)</option>
                    <option value="2026-CY">CY 2026 (Jan 2026 - Dec 2026)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Default Country</label>
                  <select defaultValue="IN" className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                    <option value="IN">India (IN)</option>
                    <option value="US">United States (US)</option>
                    <option value="AE">United Arab Emirates (AE)</option>
                    <option value="GB">United Kingdom (GB)</option>
                  </select>
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-800 dark:text-slate-200">Stock Valuation & Costing Policy</label>
                  {isCostPolicyLocked && (
                    <span className="text-[10px] font-bold text-warning-text dark:text-warning-text bg-warning/10 dark:bg-warning px-2 py-0.5 rounded flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Locked by Live Transactions
                    </span>
                  )}
                </div>

                <select
                  disabled={isCostPolicyLocked}
                  value={costingPolicy}
                  onChange={(e) => setCostingPolicy(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg text-sm disabled:opacity-60"
                >
                  <option value="FIFO">First-In, First-Out (FIFO)</option>
                  <option value="WEIGHTED_AVG">Weighted Average Cost (WAC)</option>
                  <option value="STANDARD_COST">Standard Costing</option>
                </select>

                <p className="text-[11px] text-slate-500">
                  Costing policy dictates inventory relief and COGS recognition algorithms. Policy is locked once live transactions exist to preserve accounting integrity.
                </p>
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2"
                >
                  <Save className="w-4 h-4" /> Save Company Profile
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default CompanySettingsPage;
